import { useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { FormProvider } from 'react-hook-form';

import { ArrowRightIcon, FormDatepicker, FormTimeSelect, Link, Text, spacing } from '@pletly/ui';
import { eachMinuteOfInterval, endOfDay, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ScheduleFormMethods } from './useScheduleForm';

interface Props {
	formMethods: ScheduleFormMethods;
}

export const Schedule = ({ formMethods }: Props) => {
	const { t } = useTranslation();
	const selectedDate = formMethods.watch('date');
	const availableTimes = useMemo(() => {
		if (!selectedDate) return [];

		return getAvailableTimes(new Date(selectedDate));
	}, [selectedDate]);

	useEffect(() => {
		const selectedTime = formMethods.getValues('time');
		if (!availableTimes.length || !selectedTime) return;
		if (!availableTimes.includes(selectedTime)) {
			formMethods.setValue('time', availableTimes[0]);
		}
	}, [selectedDate, availableTimes, formMethods]);

	return (
		<View>
			<FormProvider {...formMethods}>
				<View style={styles.form}>
					<FormDatepicker name="date" label={t('post.new.date')} status="basic" />
					<FormTimeSelect
						name="time"
						availableTimes={availableTimes}
						label={t('post.new.time')}
						disabled={!selectedDate}
						status="basic"
					/>
				</View>
			</FormProvider>
			<Link screen="ScheduledPosts" style={styles.link}>
				<Text category="h6">{t('post.new.show-scheduled-posts')}</Text>
				<ArrowRightIcon />
			</Link>
		</View>
	);
};

const getAvailableTimes = (date: Date) => {
	const startDate = isToday(date) ? new Date() : date;

	const times = eachMinuteOfInterval({
		start: startDate,
		end: endOfDay(date),
	}).filter((time) => time.getMinutes() % 15 === 0);

	return times;
};

const styles = StyleSheet.create({
	form: { gap: spacing(3) },
	link: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing(0.5),
		alignSelf: 'flex-start',
		marginTop: spacing(8),
	},
});
